import { useCallback, useMemo, type Dispatch, type SetStateAction } from 'react';
import type { Employee, EmployeeSession, EmployeeSessionChannel } from '../../types/employee';
import { verifyPin } from '../../services/pinService';
import type { LoginResult } from './workforceTypes';

export function useWorkforceSession(
  employees: Employee[],
  /**
   * Persists the hashed PIN through the employee source, so it lands in Convex
   * or localStorage depending on configuration. This hook no longer mutates an
   * in-memory employee array, because Convex owns that list.
   */
  setEmployeePinOnSource: (employeeId: string, pin: string) => Promise<boolean>,
  session: EmployeeSession | null,
  setSession: Dispatch<SetStateAction<EmployeeSession | null>>
) {
  const activeEmployee = useMemo(
    () => (session ? employees.find((e) => e.id === session.employeeId) ?? null : null),
    [session, employees]
  );

  const logout = useCallback(() => setSession(null), [setSession]);

  const openSession = useCallback(
    (employeeId: string, channel: EmployeeSessionChannel) => {
      setSession({
        employeeId,
        channel,
        startedAt: new Date().toISOString(),
      });
    },
    [setSession]
  );

  const login = useCallback(
    (employeeId: string, pin: string, channel: EmployeeSessionChannel): LoginResult => {
      const employee = employees.find((e) => e.id === employeeId);
      if (!employee) return { ok: false, error: 'not-found' };
      if (employee.status !== 'active') return { ok: false, error: 'inactive' };
      // Employees imported from payroll have no kiosk PIN until a manager sets
      // one; separating this from a wrong PIN lets the UI offer PIN setup.
      if (!employee.pinHash) return { ok: false, error: 'pin-not-set' };
      if (!verifyPin(pin, employee.pinHash)) return { ok: false, error: 'invalid-pin' };

      openSession(employeeId, channel);
      return { ok: true };
    },
    [employees, openSession]
  );

  /** Used right after a PIN setup/reset, where re-entering the PIN is redundant. */
  const establishSession = useCallback(
    (employeeId: string, channel: EmployeeSessionChannel): LoginResult => {
      const employee = employees.find((e) => e.id === employeeId);
      if (!employee) return { ok: false, error: 'not-found' };
      if (employee.status !== 'active') return { ok: false, error: 'inactive' };
      openSession(employeeId, channel);
      return { ok: true };
    },
    [employees, openSession]
  );

  const setEmployeePin = useCallback(
    async (employeeId: string, pin: string) => {
      const ok = await setEmployeePinOnSource(employeeId, pin);
      return ok ? { ok: true as const } : { ok: false as const, error: 'invalid-pin' as const };
    },
    [setEmployeePinOnSource]
  );

  return {
    session,
    activeEmployee,
    login,
    establishSession,
    logout,
    setEmployeePin,
  };
}
